import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { View, Text, StyleSheet, TextInput, Pressable } from '../lib/react-native-compat';
import { selectedJobAtom, jobUserDataAtom, actualHoursTestAtom, budgetHoursTestAtom, updatedBudgetHoursTestAtom, eacHoursTestAtom, jobCostDetailAtom, dataSourceVersionAtom, projectCompleteAtom, selectedJobDataAtom, selectedVersionAtom, phasedForecastAtom, selectedVersionLockedAtom, jobPhaseCodesAtom } from '../store/company';
import { useAtom } from 'jotai';
import moment from 'moment';
import { loadUserData, f, fNum, pNum, sum, cN, updateUserDataArray } from '../db/company'
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import XLSX from 'xlsx';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const s = (w = null) => StyleSheet.create({
    rate: {
        width: 80,
        height: 24,
        border: '1px solid #aeadaf',
        padding: 4,
    },
    rateB: {
        width: 80,
        height: 24,
        border: '1px solid white',
        padding: 4,
    },
    span: {
        width: 150,
        padding: 4,
        height: 24,
        alignItems: 'center',
        textAlign: 'right',
    },
    spanS: {
        width: 120,
        padding: 4,
        height: 24,
        alignItems: 'center',
        textAlign: 'right',
    },
    break: {
        width: 0,
        padding: 4,
        height: 24
    },
    total: {
        width: 110,
        border: '1px solid #aeadaf',
        textAlign: 'center',
        padding: 4,
    },
    title: {
        width: w ? w : 100,
        border: '1px solid #aeadaf',
        borderRight: 0,
        textAlign: 'center',
        padding: 4,
    },
    spanT: {
        width: '100%',
        padding: 4,
        height: 24,
        fontSize: 10,
        fontWeight: 'bold',
        textAlign: 'right',
        border: '1px solid lightgrey',
        borderRight: 0
    },
})

const LaborGraphs = (a) => {
    const laborRef = useRef()
    if (a?.photo) {
        a.setJobRef(a.job, 'laborGraphs', laborRef)
    }
    const [phases, setPhases] = useState([{}, {}, {}])
    const [phaseDescriptions, setPhaseDescriptions] = useState([])
    const [currentBudgetDollars, setCurrentBudgetDollars] = useState(0)
    const [selectedJob, setSelectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion, setSelectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJobData, setSelectedJobData] = useAtom(selectedJobDataAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [jobCostDetail, setJobCostDetail] = useAtom(jobCostDetailAtom);
    const [versions, setVersions] = useAtom(dataSourceVersionAtom)
    const [projectComplete, setProjectComplete] = useAtom(projectCompleteAtom);
    const [projectWeeks, setProjectWeeks] = useState([])
    const [projectDates, setProjectDates] = useState([])
    const [userData, setUserData] = useState({})
    const [regular, setRegular] = useAtom(phasedForecastAtom)
    const [lockedVersion, setLockedVersion] = useAtom(selectedVersionLockedAtom)
    const [actualHoursTest, setActualHoursTest] = useAtom(actualHoursTestAtom)
    const [budgetHoursTest, setBudgetHoursTest] = useAtom(budgetHoursTestAtom)
    const [updatedBudgetHoursTest, setUpdatedBudgetHoursTest] = useAtom(updatedBudgetHoursTestAtom)
    const [eacHoursTest, setEacHoursTest] = useAtom(eacHoursTestAtom)
    const [versionInfo, setVersionInfo] = useState({})
    const [weeksHours, setWeeksHours] = useState({})
    const [weeksCount, setWeeksCount] = useState({})
    const [totalDayZero, setTotalDayZero] = useState(0)
    const [totalCO, setTotalCO] = useState(0)
    const localChanges = useRef([])
    const focusedId = useRef(null)
    const [forecastedWeekHours, setForecastedWeekHours] = useState({})
    const [forecastedWeekCount, setForecastedWeekCount] = useState({})
    const [disciplines, setDisciplines] = useState({})
    const [sections, setSections] = useState([])
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom)
    const [cumulatives, setCumulatives] = useState({})
    const [barData, setBarData] = useState([])
    const [barData2, setBarData2] = useState([])
    const [barData3, setBarData3] = useState([])
    const [lineData, setLineData] = useState({ actualCumulative: [], plannedCumulative: [], forecastCumulative: [], futureForecastedCumulative: [] });
    const [totalRemainingDayZero, setTotalRemainingDayZero] = useState(0)
    const [totalRemainingCO, setTotalRemainingCO] = useState(0)
    const [xAxisLabels, setXAxisLabels] = useState([])
    const [spaceWidth, setSpaceWidth] = useState(50)
    const [maxValue, setMaxValue] = useState(40000)
    const [historicalWeeks, setHistoricalWeeks] = useState([])
    const [graphWidth, setGraphWidth] = useState(2000)
    const graphWidthRef = useRef(2000)
    const [maxHeightBar, setMaxHeightBar] = useState(0)
    const [trendingEACTotal, setTrendingEACTotal] = useState(null)

    // The Labor S Curve keeps its own long-standing colours (peach actual, grey
    // planned, sky forecast) rather than the Strike series palette.
    const colors = {
        actual: '#F4B183',
        planned: '#A5A5A5',
        forecast: '#7CAFDD',
        currentPlanned: '#597097',
        plannedCumulative: '#b4b4b4',
        actualCumulative: '#f4b183',
        forecastCumulative: '#7CAFDD',
        trendingEAC: '#6b7280'
    };

    const generateChartData = (cums) => {
        if (!cums || !Object.keys(cums).length) return;
        const barData = [];
        const barData2 = [];
        const barData3 = [];
        const lineData = {
            actualCumulative: [],
            forecastCumulative: [],
            plannedCumulative: [],
            futureForecastedCumulative: [],
            zeros: []
        };

        let weekIndex = 0;
        let cumulativeForecast = 0;
        let bottomColumns = []
        let lastActualCumulative = projectWeeks.length && projectWeeks[projectWeeks.length - 1] ? pNum(cums[projectWeeks[projectWeeks.length - 1]]?.actual) : 0
        let maxHeightBar = 0
        let totalPlanned = 0
        let totalApproved = 0
        //console.log('planned from userdta', userData?.laborGraphs)

        projectWeeks.forEach((week, index) => {
            const plannedHours = userData?.laborGraphs?.[week]?.plannedHours ? pNum(userData?.laborGraphs?.[week]?.plannedHours) : 0
            const coHours = userData?.laborGraphs?.[projectWeeks[week]]?.coHours || 0
            totalPlanned = totalPlanned + pNum(plannedHours)
            totalApproved = totalApproved + pNum(coHours)
            //if (plannedHours) console.log('plannedHours', plannedHours)
            // Create bar data for each week
            barData.push({ value: weeksHours[week], frontColor: colors.actual, spacing: 15 })
            barData2.push({ value: plannedHours, frontColor: colors.planned, spacing: 15 })
            barData3.push({ value: forecastedWeekHours[week], frontColor: colors.forecast, spacing: 15 })

            //console.log('maxHeightBar', maxHeightBar, weeksHours[week], plannedHours, forecastedWeekHours[week])
            maxHeightBar = weeksHours[week] > maxHeightBar ? !isNaN(pNum(weeksHours[week])) ? pNum(weeksHours[week]) : maxHeightBar : maxHeightBar
            maxHeightBar = plannedHours > maxHeightBar ? !isNaN(pNum(plannedHours)) ? pNum(plannedHours) : maxHeightBar : maxHeightBar
            maxHeightBar = forecastedWeekHours[week] > maxHeightBar ? !isNaN(pNum(forecastedWeekHours[week])) ? pNum(forecastedWeekHours[week]) : maxHeightBar : maxHeightBar

            // Create line data for cumulative values
            const isBeforeForecast = moment(week).isBefore(moment(versionInfo?.Version_Start_DateTime).startOf('week'));
            lineData.actualCumulative.push({ label: week, value: isBeforeForecast ? cums[week]?.actual : null });
            lineData.forecastCumulative.push({ label: week, value: cums[week]?.planned });
            lineData.plannedCumulative.push({ label: week, value: cums[week]?.currentPlanned });
            lineData.zeros.push({ label: week, value: 0 });
            moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) ? lastActualCumulative = pNum(forecastedWeekHours[week]) + lastActualCumulative : null
            moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) ? lineData.futureForecastedCumulative.push({ label: week, value: lastActualCumulative }) : lineData.futureForecastedCumulative.push({ label: week, value: cums[week]?.actual });
            //console.log('week', week, moment(week), moment(versionInfo?.Version_Start_DateTime).startOf('week').format('MM/DD/YYYY'), moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')))
            cumulativeForecast = moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) ? cumulativeForecast + cums[week]?.forecasted : cumulativeForecast

            weekIndex++;

            const weekData = {
                label: moment(week).format('M/D/YY'),
                actual: weeksHours[week],
                planned: plannedHours,
                forecasted: forecastedWeekHours[week],
                forecastedCumulative: moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) ? lastActualCumulative : cums[week]?.actual,
                plannedCumulative: cums[week]?.planned,
                currentPlannedCumulative: cums[week]?.currentPlanned,
                actualCumulative: cums[week]?.actual,
                forecastCumulative: cumulativeForecast
            }
            bottomColumns.push(weekData)
        })
        setActualHoursTest(bottomColumns[bottomColumns.length - 1]?.actualCumulative)
        setBudgetHoursTest(bottomColumns[bottomColumns.length - 1]?.plannedCumulative)
        setUpdatedBudgetHoursTest(bottomColumns[bottomColumns.length - 1]?.currentPlannedCumulative)
        setEacHoursTest(bottomColumns[bottomColumns.length - 1]?.forecastedCumulative)
        //console.log('bottomColumns', barData)
        setHistoricalWeeks(bottomColumns)
        //console.log('maxHeightBar', maxHeightBar)
        setMaxHeightBar(maxHeightBar)

        let maxValue = lineData.actualCumulative.length > 0 ? (lineData.actualCumulative.filter(d => d.value != null).pop()?.value || 0) : 0
        maxValue = lineData.forecastCumulative.length > 0 ? lineData.forecastCumulative[lineData.forecastCumulative.length - 1].value > maxValue ? lineData.forecastCumulative[lineData.forecastCumulative.length - 1].value : maxValue : maxValue
        maxValue = lineData.plannedCumulative.length > 0 ? lineData.plannedCumulative[lineData.plannedCumulative.length - 1].value > maxValue ? lineData.plannedCumulative[lineData.plannedCumulative.length - 1].value : maxValue : maxValue
        maxValue = lineData.futureForecastedCumulative.length > 0 ? lineData.futureForecastedCumulative[lineData.futureForecastedCumulative.length - 1].value > maxValue ? lineData.futureForecastedCumulative[lineData.futureForecastedCumulative.length - 1].value : maxValue : maxValue
        if (trendingEACTotal && trendingEACTotal > maxValue) maxValue = trendingEACTotal
        setMaxValue(maxValue + 4000)

        const newGraphWidth = (lineData.forecastCumulative.length - 1) * 50 + 210;
        if (newGraphWidth !== graphWidthRef.current) {
            graphWidthRef.current = newGraphWidth;
            setGraphWidth(newGraphWidth);
        }

        const xAxisLabel = projectWeeks.map((item, index) =>
            index % 10 === 0 ? item : ''
        );
        //console.log('xAxisLabel', xAxisLabel.length, projectWeeks.length, lineData.actualCumulative.length, lineData.forecastCumulative.length, lineData.plannedCumulative.length, lineData.futureForecastedCumulative.length)
        setXAxisLabels(xAxisLabel)

        //console.log('barData', barData, 'lineData', lineData, 'cumulatives', cumulatives)
        setBarData(barData);
        setBarData2(barData2);
        setBarData3(barData3);
        setLineData(lineData);
        //console.log('totalPlanned', totalPlanned, totalApproved)
        
        setTotalRemainingDayZero(fNum(pNum(totalDayZero) - pNum(totalPlanned)))
        setTotalRemainingCO(fNum(pNum(totalCO) - pNum(totalApproved)))
    }


    useEffect(() => {
        if (selectedJob && selectedVersion) {
            loadUserData({ id: selectedJob, forecastGuid: selectedVersion }).then((data) => {
                setJobUserData(data?.data);
            })
        }
    }, [selectedJob, selectedVersion])

    // useEffect(() => {
    //     console.log('is phased', regular)
    // }, [regular])

    useEffect(() => {
        versions && versions.length > 0 && setVersionInfo(versions.find(v => v.Version_GUID === selectedVersion))
    }, [versions]);

    useEffect(() => {
        const uData = {
            cos: {},
            dayZero: {},
            burnTool: {},
            laborGraphs: {},
            phaseHours: {},
            phaseQuantities: {},
            scorecard: {}
        }
        if (jobUserData && jobUserData.length > 0) {
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Burn Tool Labor' || d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                    uData.burnTool[d?.Forecast_Row_ID] = d
                }

                if (d?.Forecast_Tab === 'Day Zero Budget') {
                    uData.dayZero[d?.Forecast_Row_ID] = d
                }

                if (d?.Forecast_Tab === 'Change Order') {
                    uData.cos[d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                }

                if (d?.Forecast_Tab === 'Labor Graphs') {
                    if (uData.laborGraphs[d?.Forecast_Row_ID]) {
                        uData.laborGraphs[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        uData.laborGraphs[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }

                if (d?.Forecast_Tab === 'Scorecard') {
                    if (uData.scorecard[d?.Forecast_Row_ID]) {
                        uData.scorecard[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    } else {
                        uData.scorecard[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        }
                    }
                }

                if (d?.Forecast_Tab === 'Phase Hours') {
                    if (!uData.phaseHours[d?.Forecast_Row_ID]) {
                        uData.phaseHours[d?.Forecast_Row_ID] = {
                            weeks: {}
                        }
                    }
                    if (d.Forecast_Field_Name.startsWith('week_')) {
                        const week = d.Forecast_Field_Name.replace('week_', '')
                        uData.phaseHours[d?.Forecast_Row_ID].weeks[week] = d.Forecast_Value
                    } else {
                        uData.phaseHours[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    }
                }

                if (d?.Forecast_Tab === 'Phase Quantities') {
                    if (!uData.phaseQuantities[d?.Forecast_Row_ID]) {
                        uData.phaseQuantities[d?.Forecast_Row_ID] = {
                            weeks: {}
                        }
                    }
                    if (d.Forecast_Field_Name.startsWith('week_')) {
                        const week = d.Forecast_Field_Name.replace('week_', '')
                        uData.phaseQuantities[d?.Forecast_Row_ID].weeks[week] = d.Forecast_Value
                    } else {
                        uData.phaseQuantities[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value
                    }
                }
            })

            console.log('uData chart', uData)

            setUserData(uData)
        }
    }, [jobUserData, regular])

    // Load disciplines from Productivity Tracker (same as productivityCombined)
    useEffect(() => {
        const uData = {}
        if (jobUserData && jobUserData.length > 0) {
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Productivity Tracker' && d.Forecast_Field_Name === 'discipline') {
                    uData[d?.Forecast_Row_ID] = d.Forecast_Value
                }
            })
        }
        setDisciplines(uData)
    }, [jobUserData])

    // Compute trending EAC hours (same formula as productivityCombined)
    useEffect(() => {
        if (!jobCostDetail || !userData?.dayZero || !sections || sections.length === 0) {
            setTrendingEACTotal(null)
            return
        }

        // Build phase day-zero budget map
        const phaseDayZero = {}
        Object.values(userData.dayZero).forEach((row) => {
            if (phaseDayZero[row['Cost_Code']]) {
                if (row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8') {
                    phaseDayZero[row['Cost_Code']].hours += pNum(row['Hours']) || 0
                }
                phaseDayZero[row['Cost_Code']].quantities = phaseDayZero[row['Cost_Code']].quantities + (isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity']))
            } else {
                phaseDayZero[row['Cost_Code']] = {
                    hours: row['Cost_Type'] && row['Cost_Type'][0] == '5' && row['Cost_Type'][1] == '1' && row['Cost_Type'][2] != '8' ? (pNum(row['Hours']) || 0) : 0,
                    quantities: isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity'])
                }
            }
        })

        // Build phase change order map
        const phaseCO = {}
        Object.values(userData.cos).forEach((row) => {
            const phase = row.Cost_Code
            const isLaborHours = row.Forecast_Field_Name === 'totalLaborHours' ||
                (String(row.Cost_Type || '').trim() === '51100' && row.Forecast_Field_Name !== 'totalEquipmentHours')
            const laborHours = isLaborHours ? (isNaN(pNum(row.Hours)) ? 0 : pNum(row.Hours)) : 0
            if (phaseCO[phase] && row.Status == 'Approved') {
                phaseCO[phase].hours += laborHours
                phaseCO[phase].budgetQty += isNaN(pNum(row.Quantity)) ? 0 : pNum(row.Quantity)
            } else if (phase && row.Status == 'Approved') {
                phaseCO[phase] = {
                    hours: laborHours,
                    budgetQty: isNaN(pNum(row.Quantity)) ? 0 : pNum(row.Quantity)
                }
            }
        })

        // Build actual hours per phase from cost detail
        const phaseActualHoursMap = {}
        Array.isArray(jobCostDetail) && jobCostDetail.forEach((row) => {
            if (row.Cost_Type_Code && row.Cost_Type_Code[0] == '5' && row.Cost_Type_Code[1] == '1' && row.Cost_Type_Code[2] != '8' && row.Phase_Code) {
                phaseActualHoursMap[row.Phase_Code] = (phaseActualHoursMap[row.Phase_Code] || 0) + pNum(row.Total_Hours)
            }
        })

        // Heavy Job installed quantities from FP document type
        const heavyJobInstalledMap = {}
        Array.isArray(jobCostDetail) && jobCostDetail.forEach((row) => {
            if (row.Document_Type == 'FP' && row.Phase_Code) {
                heavyJobInstalledMap[row.Phase_Code] = (heavyJobInstalledMap[row.Phase_Code] || 0) + (pNum(row.Total_Hours) || 0)
            }
        })

        // Sum trending EAC across all phases
        let total = 0
        let hasAnyPhase = false
        sections.forEach((disciplinePhases) => {
            disciplinePhases.forEach((p) => {
                const code = p.code
                const phaseActualHours = phaseActualHoursMap[code] || 0
                const dayZeroHours = phaseDayZero[code]?.hours || 0
                const coHours = phaseCO[code]?.hours || 0
                const budgetHours = pNum(dayZeroHours) + pNum(coHours)
                const dayZeroQty = phaseDayZero[code]?.quantities || 0
                const coQty = phaseCO[code]?.budgetQty || 0
                const plug = pNum(userData.scorecard?.[code]?.plug) || 0
                const totalQuantity = (isNaN(pNum(dayZeroQty)) ? 0 : pNum(dayZeroQty)) + (isNaN(pNum(coQty)) ? 0 : pNum(coQty)) + (isNaN(pNum(plug)) ? 0 : pNum(plug))
                const userInstalled = userData.scorecard?.[code]?.installed && !isNaN(pNum(userData.scorecard?.[code]?.installed)) ? fNum(pNum(userData.scorecard?.[code]?.installed)) : ''
                const heavyJobInstalled = heavyJobInstalledMap[code] || 0
                const totalInstalledQuantity = (isNaN(pNum(heavyJobInstalled)) ? 0 : pNum(heavyJobInstalled)) + (isNaN(pNum(userInstalled)) ? 0 : pNum(userInstalled))
                const percentComplete = totalQuantity > 0 ? (isNaN(pNum(totalInstalledQuantity)) ? 0 : 100 * pNum(totalInstalledQuantity)) / (isNaN(pNum(totalQuantity)) ? 0 : pNum(totalQuantity)) : ''
                const budgetHoursQty = totalQuantity ? fNum(pNum(budgetHours) / pNum(totalQuantity)) : ''
                const actualHoursQtyValue = totalInstalledQuantity && pNum(totalInstalledQuantity) > 0 ? (percentComplete < 25 ? budgetHoursQty : fNum(pNum(phaseActualHours) / pNum(totalInstalledQuantity))) : ''

                const trendingEACHours = percentComplete && percentComplete > 25
                    ? fNum(pNum(actualHoursQtyValue) * pNum(totalQuantity), 1)
                    : pNum(phaseActualHours) > pNum(budgetHours)
                        ? fNum(pNum(phaseActualHours), 1)
                        : fNum(pNum(budgetHours), 1)
                total += pNum(trendingEACHours)
                if (budgetHours > 0 || phaseActualHours > 0) hasAnyPhase = true
            })
        })

        setTrendingEACTotal(hasAnyPhase ? total : null)
    }, [jobCostDetail, userData, sections])

    // Build sections from disciplines — dynamically from all data sources (matching productivityCombined)
    useEffect(() => {
        const allCodes = new Set()
        // Phase codes from job cost detail (actual hours)
        if (Array.isArray(jobCostDetail)) {
            jobCostDetail.forEach(row => {
                if (row.Phase_Code && row.Phase_Code !== '1000' && row.Phase_Code !== '899999' && row.Phase_Code !== '899998' && row.Phase_Code !== '990000') {
                    allCodes.add(row.Phase_Code)
                }
            })
        }
        // Phase codes from day zero budget
        if (userData?.dayZero) {
            Object.values(userData.dayZero).forEach(item => {
                if (item?.Cost_Code && item.Cost_Code !== '1000' && item.Cost_Code !== '990000'
                    && item.Cost_Type?.toString()[0] === '5' && item.Cost_Type?.toString()[1] === '1') {
                    allCodes.add(item.Cost_Code)
                }
            })
        }
        // Phase codes from change orders
        if (userData?.cos) {
            Object.values(userData.cos).forEach(co => {
                if (co?.Cost_Code && co.Cost_Code !== '1000' && co.Cost_Code !== '990000'
                    && co.Cost_Type?.toString()[0] === '5' && co.Cost_Type?.toString()[1] === '1') {
                    allCodes.add(co.Cost_Code)
                }
            })
        }
        // Phase codes from productivity tracker discipline mapping
        Object.keys(disciplines).forEach(code => allCodes.add(code))

        const phases = [...allCodes].sort().map(code => ({
            code,
            description: jobPhaseCodes[code] || ''
        }))

        const disciplineSections = {}
        phases.forEach((phase) => {
            phase.discipline = disciplines[phase.code] ? disciplines[phase.code] : 'Other'
            if (!disciplines[phase.code] && disciplineSections['Other']) {
                disciplineSections['Other'].push(phase)
            } else if (!disciplines[phase.code]) {
                disciplineSections['Other'] = [phase]
            } else if (disciplineSections[disciplines[phase.code]]) {
                disciplineSections[disciplines[phase.code]].push(phase)
            } else {
                disciplineSections[disciplines[phase.code]] = [phase]
            }
        })
        setSections(Object.values(disciplineSections))
    }, [disciplines, jobCostDetail, userData, jobPhaseCodes])

    useEffect(() => {
        let dateSections = {}
        let uniqueWeeks = {}
        
        let plannedStart = moment(selectedJobData?.Planned_Start_Date)

        jobCostDetail?.forEach((row) => {
            const rowDate = moment(row.Consolidated_Date)//.add(12, 'hours')
            if (row.Cost_Type_Code >= "51000" && row.Cost_Type_Code <= "51799" && row.Job_Cost > 0) {
                if (rowDate.isBefore(plannedStart)) {
                    console.log('setting newer start date')
                    plannedStart = rowDate
                }
            }
        });        
        
        const plannedFinish = moment(projectComplete)

        let currentDay = plannedStart
        let currentWeek = plannedStart.startOf('isoWeek')
        const days = plannedFinish.diff(plannedStart, 'days')
        for (let i = 0; i < days; i++) {
            const day = currentDay.clone()
            const week = currentDay.clone()
            day.add(i, 'days')
            week.add(i, 'days')
            currentWeek = week.startOf('isoWeek')
            const currentDayFormatted = day.format('YYYY-MM-DD')
            uniqueWeeks[currentWeek.format('YYYY-MM-DD')] = 1
            dateSections[currentDayFormatted] = [currentWeek.format('YYYY-MM-DD')]
        }

        //console.log('dateSections', dateSections)
        setProjectDates(dateSections)
        setProjectWeeks(Object.keys(uniqueWeeks))
    }, [selectedJobData, projectComplete, jobCostDetail])

    useEffect(() => {
        let cums = cumulatives;
        if (userData.laborGraphs) {
            cums = calculateTotals();
        }
        generateChartData(cums);
    }, [weeksHours, userData, forecastedWeekHours])

    const calculateTotals = () => {
        let actualHours = 0
        let plannedHours = 0
        let currentPlannedHours = 0
        let forecastedHours = 0
        const weekTotals = {}
        //console.log('project weeks', projectWeeks)
        projectWeeks.forEach((week) => {
            actualHours = actualHours + (weeksHours[week] ? weeksHours[week] : 0)
            plannedHours = plannedHours + (userData?.laborGraphs && userData.laborGraphs[week]?.plannedHours ? pNum(userData?.laborGraphs[week]?.plannedHours) : 0)
            currentPlannedHours = currentPlannedHours + (userData?.laborGraphs && userData.laborGraphs[week]?.plannedHours || userData?.laborGraphs[week]?.coHours ? pNum(userData?.laborGraphs[week]?.plannedHours) + pNum(userData?.laborGraphs[week]?.coHours) : 0)
            forecastedHours = moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) ? forecastedHours + (forecastedWeekHours?.[week] ? forecastedWeekHours[week] : 0) : forecastedHours + (weeksHours[week] ? weeksHours[week] : 0)
            weekTotals[week] = {
                actual: actualHours,
                planned: plannedHours,
                currentPlanned: currentPlannedHours,
                forecasted: forecastedHours,
            }
        })
        //console.log('forecastedHours', forecastedWeekHours, weekTotals)
        setCumulatives(weekTotals)
        return weekTotals
    }

    useEffect(() => {
        const weeksHours = {}
        const weeksCount = {}
        const weeksStaffId = {}
        const forecastedWeekHours = {}
        const forecastedWeekCount = {}
        let one = false
        jobCostDetail?.forEach((row) => {
            if (row.Cost_Type_Code >= '51000' && row.Cost_Type_Code <= '51799') {
                if (!one) {
                    //console.log('row', row)
                    one = true
                }
                const rowDate = moment(row.Consolidated_Date).startOf('isoWeek')//.add(12, 'hours')
                const rowDateFormatted = rowDate.format('YYYY-MM-DD')
                if (weeksHours[rowDateFormatted]) {
                    weeksHours[rowDateFormatted] = weeksHours[rowDateFormatted] + pNum(row.Total_Hours)
                } else {
                    weeksHours[rowDateFormatted] = pNum(row.Total_Hours)
                }

                if (!weeksStaffId[row.Staff_ID]) {
                    weeksStaffId[row.Staff_ID] = true
                    if (weeksCount[rowDateFormatted]) {
                        weeksCount[rowDateFormatted] = weeksCount[rowDateFormatted] + 1
                    } else {
                        weeksCount[rowDateFormatted] = 1
                    }
                }
            }
        })
        setWeeksCount(weeksCount)
        //console.log('weeksHours', weeksHours)
        setWeeksHours(weeksHours)

        let dayZeroTotal = 0
        let coTotal = 0
        userData.dayZero && Object.values(userData.dayZero).forEach((row) => {
            if (row['Value_Amount'] && (row['Cost_Type'] == '51100') || (row['Cost_Type'] == '51200') || (row['Cost_Type'] == '51300') || (row['Cost_Type'] == '51400') || (row['Cost_Type'] == '51500') || (row['Cost_Type'] == '51600') || (row['Cost_Type'] == '51700')) {
                dayZeroTotal = dayZeroTotal + (pNum(row['Hours']) ? pNum(row['Hours']) : 0)
            }
        })
        setTotalDayZero(dayZeroTotal)

        userData.cos && Object.values(userData.cos).forEach((row) => {
            if (row['Hours'] && row['Status'] === 'Approved' && (row['Forecast_Field_Name'] === 'totalLaborHours' ||
                (String(row['Cost_Type'] || '').trim() === '51100' && row['Forecast_Field_Name'] !== 'totalEquipmentHours'))) {
                coTotal = coTotal + (pNum(row['Hours']) ? pNum(row['Hours']) : 0)
            }
        })
        setTotalCO(coTotal)


        let userPlanned = 0
        let userApprovedCO = 0
        //console.log('userData', userData)
        if (regular == 'phased') {
            userData.burnTool && Object.keys(userData.burnTool).forEach((row) => {
                const weekRow = row.includes('=')
                const week = row.split('=')[1]
                const phase = row.split('=')[0]
                const phasedLabor = userData.burnTool
                const phaseDetails = phase ? phasedLabor[phase] : {}
                const withinProject = /*moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) &&*/ moment(week).isBefore(moment(projectComplete).endOf('week'))
                //const withinStartStop = moment(week).isAfter(moment(phaseDetails?.startDate).startOf('week')) && moment(week).isBefore(moment(phaseDetails?.endDate).endOf('week'))
                const h = phasedLabor[row]?.Number_Hours ? phasedLabor[row].Number_Hours : 0//phasedLabor[row].hoursPerWeekEst
                const bR = phasedLabor[row]?.Rate ? phasedLabor[row].Rate : 0
                const cS = phasedLabor[row] && phasedLabor[row]?.Number_Units ? phasedLabor[row].Number_Units : 0//phaseDetails ? phaseDetails?.Number_Units : 0
                //console.log('week', week, 'withinProject', withinProject, row, (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h)  * pNum(cS) : 0))
                if (weekRow && phasedLabor[row] && h && bR && cS && withinProject) {
                    if (week && forecastedWeekHours[week]) {
                        forecastedWeekHours[week] = forecastedWeekHours[week] + (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    } else if (week) {
                        forecastedWeekHours[week] = (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                    }

                    if (week && forecastedWeekCount[week]) {
                        forecastedWeekCount[week] = forecastedWeekCount[week] + (!isNaN(pNum(cS)) ? pNum(cS) : 0)
                    } else if (week) {
                        forecastedWeekCount[week] = (!isNaN(pNum(cS)) ? pNum(cS) : 0)
                    }
                }
            })
            //console.log('weekhours phased', forecastedWeekHours)
        } else {
            userData.burnTool && Object.keys(userData.burnTool).forEach((row) => {
                const week = row.split('*')[0]
                const h = userData.burnTool[row]?.Number_Hours ? userData.burnTool[row].Number_Hours : 0
                const bR = userData.burnTool[row]?.Rate ? userData.burnTool[row].Rate : 0
                const cS = userData.burnTool[row]?.Number_Units ? userData.burnTool[row].Number_Units : 0
                if (week && forecastedWeekHours[week]) {
                    forecastedWeekHours[week] = forecastedWeekHours[week] + (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                } else if (week) {
                    forecastedWeekHours[week] = (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(cS) : 0)
                }

                if (week && forecastedWeekCount[week]) {
                    forecastedWeekCount[week] = forecastedWeekCount[week] + (!isNaN(pNum(cS)) ? pNum(cS) : 0)
                } else if (week) {
                    forecastedWeekCount[week] = (!isNaN(pNum(cS)) ? pNum(cS) : 0)
                }

            })
            //console.log('not phased burn tool', forecastedWeekHours)
        }
        setForecastedWeekHours(forecastedWeekHours)
        setForecastedWeekCount(forecastedWeekCount)
    }, [jobCostDetail, userData])


    const LaborWeek = (props) => {
        const [plannedHours, setPlannedHours] = useState(userData?.laborGraphs?.[props.week]?.plannedHours || '')
        const [coHours, setCoHours] = useState(userData?.laborGraphs?.[props.week]?.coHours || '')
        const [staffCount, setStaffCount] = useState(userData?.laborGraphs?.[props.week]?.staffCount || '')
        const week = props.week
        const [textTimeout, setTextTimeout] = useState(null)
        const [localFocusedId, setLocalFocusedId] = useState(focusedId.current)
        const inputsRef = useRef([])

        const updateLocalChanges = async (field, value) => {
            if (selectedJob && value && value != 'undefined') {
                let data = {
                    userId: '154045',
                    userName: 'Seth Bailey',
                    fieldName: field,
                    tab: "Labor Graphs",
                    rowId: week,
                    version: selectedVersion + '',
                    value: value + '',
                    id: selectedJob,
                    lockedVersion: false
                }
                console.log('saving data', data)
                updateUserDataArray(data)

                const stateUpdate = {
                    Forecast_Field_Name: field,
                    Forecast_Value: value,
                    Forecast_Row_ID: week,
                    Forecast_Tab: "Labor Graphs",
                    BU_ID: selectedJob,
                    key: week + '*' + field
                }

                let isRecorded = false
                let lChanges = {}

                localChanges.current.forEach((d) => {
                    if (d.Forecast_Row_ID == week && d.Forecast_Field_Name == field && d.Forecast_Tab == "Labor Graphs") {
                        isRecorded = true
                        d.Forecast_Value = value
                    }
                    lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name] = { ...d, key: d.Forecast_Row_ID + '*' + d.Forecast_Field_Name }
                })

                if (!isRecorded) {
                    lChanges[week + '*' + field] = stateUpdate
                }

                localChanges.current = Object.values(lChanges)
            }
        }

        const updateState = () => {
            let lChanges = {}
            localChanges.current.forEach((d) => {
                lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name] = { ...d, key: d.Forecast_Row_ID + '*' + d.Forecast_Field_Name }
            })

            const uData = JSON.parse(JSON.stringify(jobUserData))
            uData.forEach((d) => {
                if (lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name] && d.Forecast_Tab == "Labor Graphs") {
                    d.Forecast_Value = lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name].Forecast_Value
                    lChanges[d.Forecast_Row_ID + '*' + d.Forecast_Field_Name].updated = true
                }
            })

            const notUpdated = Object.values(lChanges).filter((d) => !lChanges[d.key].updated)
            console.log('not updated', notUpdated)
            setJobUserData([...uData, ...notUpdated])
        }

        useEffect(() => {
            if (focusedId.current == (week + '-plannedHours')) {
                inputsRef.current[week + '-plannedHours'].focus()
            } else if (focusedId.current == (week + '-coHours')) {
                inputsRef.current[week + '-coHours'].focus()
            } else if (focusedId.current == (week + '-staffCount')) {
                inputsRef.current[week + '-staffCount'].focus()
            }
        }, [localFocusedId])

        return (
            <View>
                <View style={{ flexDirection: 'row' }}>
                    <Text style={s(120).title}>{props.i}</Text>
                    <Text style={s(120).title}>{props.week}</Text>
                    <Text style={s(120).title}>{weeksHours[props.week] ? fNum(weeksHours[props.week]) : ''}</Text>
                    <View style={{ position: 'relative' }}>
                        <View style={{ width: 3, height: 20, backgroundColor: 'grey', left: 5, top: 4, position: 'absolute' }}></View>
                        <TextInput style={s(120).title}
                            value={plannedHours}
                            ref={e => { inputsRef.current[week + '-plannedHours'] = e }}
                            onFocus={() => { focusedId.current = week + '-plannedHours' }}  // Track the input when focused
                            onChangeText={t => {
                                setPlannedHours(t)
                                clearTimeout(textTimeout)
                                updateLocalChanges('plannedHours', t)
                                setLocalFocusedId(week + '-plannedHours')
                                if (t.length > 0) {
                                    setTextTimeout(setTimeout(() => {
                                        updateState()
                                    }, 1000))
                                }
                            }}
                        ></TextInput>
                    </View>
                    <View style={{ position: 'relative' }}>
                        <View style={{ width: 3, height: 20, backgroundColor: 'grey', left: 5, top: 4, position: 'absolute' }}></View>
                        <TextInput style={s(120).title}
                            value={coHours}
                            ref={e => { inputsRef.current[week + '-coHours'] = e }}
                            onFocus={() => { focusedId.current = week + '-coHours' }}  // Track the input when focused
                            onChangeText={t => {
                                setCoHours(t)
                                clearTimeout(textTimeout)
                                updateLocalChanges('coHours', t)
                                setLocalFocusedId(week + '-coHours')
                                if (t.length > 0) {
                                    setTextTimeout(setTimeout(() => {
                                        updateState()
                                    }, 1000))
                                }
                            }}
                        ></TextInput>
                    </View>
                    <Text style={s(120).title}>{forecastedWeekHours[week] ? fNum(forecastedWeekHours[week]) : ''}</Text>
                    <View style={{ width: 20 }}></View>
                    <Text style={s(120).title}>{cumulatives[week]?.actual ? fNum(cumulatives[week]?.actual) : ''}</Text>
                    <Text style={s(120).title}>{cumulatives[week]?.planned ? fNum(cumulatives[week]?.planned) : ''}</Text>
                    <Text style={s(120).title}>{cumulatives[week]?.currentPlanned ? fNum(cumulatives[week]?.currentPlanned) : ''}</Text>
                    <Text style={s(120).title}>{cumulatives[week]?.forecasted ? fNum(cumulatives[week]?.forecasted) : ''}</Text>
                    {/* <View style={{ width: 20 }}></View>
                    <Text style={s(120).title}>{weeksCount[week] ? fNum(weeksCount[week]) : ''}</Text>
                    <View style={{ position: 'relative' }}>
                        <View style={{ width: 3, height: 20, backgroundColor: 'grey', left: 5, top: 4, position: 'absolute' }}></View>
                        <TextInput style={s(120).title}
                            value={staffCount}
                            ref={e => { inputsRef.current[week + '-staffCount'] = e }}
                            onFocus={() => { focusedId.current = week + '-staffCount' }}  // Track the input when focused
                            onChangeText={t => {
                                setStaffCount(t)
                                clearTimeout(textTimeout)
                                updateLocalChanges('staffCount', t)
                                setLocalFocusedId(week + '-staffCount')
                                if (t.length > 0) {
                                    setTextTimeout(setTimeout(() => {
                                        updateState()
                                    }, 1000))
                                }
                            }}
                        ></TextInput>
                    </View>
                    <Text style={s(120).title}>{forecastedWeekCount[week] ? fNum(forecastedWeekCount[week]) : ''}</Text> */}

                </View>
            </View>
        )
    }

    const HistoricalRow = (props) => {
        const isLastWeek = props.index == projectWeeks.length - 1
        return (
            <View style={{ width: spaceWidth }}>
                <Text style={[s().spanT, { fontSize: 10, padding: 4, borderRight: isLastWeek ? '1px solid lightgrey' : 'none' }]}>
                    {props.row.label}
                </Text>
                <Text style={[s().spanT, { fontSize: 10, padding: 4, borderRight: isLastWeek ? '1px solid lightgrey' : 'none' }]}>
                    {props.row.actual ? fNum(pNum(props.row.actual), 1) : ''}
                </Text>
                <Text style={[s().spanT, { fontSize: 10, padding: 4, borderRight: isLastWeek ? '1px solid lightgrey' : 'none' }]}>
                    {props.row.planned ? fNum(pNum(props.row.planned), 1) : ''}
                </Text>
                <Text style={[s().spanT, { fontSize: 10, padding: 4, borderRight: isLastWeek ? '1px solid lightgrey' : 'none' }]}>
                    {props.row.forecasted ? fNum(pNum(props.row.forecasted), 1) : ''}
                </Text>
                <Text style={[s().spanT, { fontSize: 10, padding: 4, borderRight: isLastWeek ? '1px solid lightgrey' : 'none' }]}>
                    {props.row.currentPlannedCumulative ? fNum(pNum(props.row.currentPlannedCumulative), 1) : ''}
                </Text>
                <Text style={[s().spanT, { fontSize: 10, padding: 4 , borderRight: isLastWeek ? '1px solid lightgrey' : 'none' }]}>
                    {props.row.plannedCumulative ? fNum(pNum(props.row.plannedCumulative), 1) : ''}
                </Text>
                <Text style={[s().spanT, { fontSize: 10, padding: 4, borderRight: isLastWeek ? '1px solid lightgrey' : 'none' }]}>
                    {props.row.actualCumulative ? fNum(pNum(props.row.actualCumulative), 1) : ''}
                </Text>
                <Text style={[s().spanT, { fontSize: 10, padding: 4, borderRight: isLastWeek ? '1px solid lightgrey' : 'none' }]}>
                    {props.row.forecastedCumulative ? fNum(pNum(props.row.forecastedCumulative), 1) : ''}
                </Text>
            </View>
        )
    }

    const validCell = (a, b) => {
        const withinRange = Math.abs(pNum(a) - pNum(b)) <= 2;
        return withinRange ? 'transparent' : '#ffcccc'; // Light red for invalid, white for valid
    };

    const exportToExcel = () => {
        try {
            // Build phases dynamically from sections (same as the table)
            const phases = sections.flat()

            // Build the data rows
            const hoursData = phases.map(phase => {
                const row = {
                    'Phase': phase.code,
                    'Description': phase.description,
                    'Discipline': disciplines[phase.code] || 'Other'
                }

                // Add week columns
                projectWeeks.forEach(week => {
                    const weekLabel = moment(week).format('M/D/YY')
                    row[weekLabel] = userData?.phaseHours?.[phase.code]?.weeks?.[week] || ''
                })

                return row
            })

            // Calculate percentage data
            const percentageData = phases.map(phase => {
                const row = {
                    'Phase': phase.code,
                    'Description': phase.description,
                    'Discipline': disciplines[phase.code] || 'Other'
                }

                // Add week percentage columns
                projectWeeks.forEach(week => {
                    const weekLabel = moment(week).format('M/D/YY')
                    const weekTotal = sections.reduce((total, sectionPhases) => {
                        return total + sectionPhases.reduce((sum, p) => {
                            return sum + pNum(userData?.phaseHours?.[p.code]?.weeks?.[week] || 0)
                        }, 0)
                    }, 0)
                    const phaseWeekHours = pNum(userData?.phaseHours?.[phase.code]?.weeks?.[week] || 0)
                    const percentage = weekTotal > 0 ? (phaseWeekHours / weekTotal) * 100 : 0
                    row[weekLabel] = percentage > 0 ? fNum(percentage, 1) : ''
                })

                return row
            })

            // Create workbook with two sheets
            const workbook = XLSX.utils.book_new()
            
            const hoursSheet = XLSX.utils.json_to_sheet(hoursData)
            XLSX.utils.book_append_sheet(workbook, hoursSheet, 'Phase Hours by Week')
            
            const percentageSheet = XLSX.utils.json_to_sheet(percentageData)
            XLSX.utils.book_append_sheet(workbook, percentageSheet, 'Percentage Distribution')

            // Save the file
            const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
            const blob = new Blob([wbout], { type: 'application/octet-stream' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `Phase_Hours_${selectedJob}_${moment().format('YYYY-MM-DD')}.xlsx`
            a.click()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            console.error('Error exporting to Excel:', error)
            alert('Error exporting to Excel. Please try again.')
        }
    }

    const handleFileUpload = async (event) => {
        const file = event.target.files[0]
        if (!file) return

        try {
            const reader = new FileReader()
            reader.onload = async (e) => {
                const data = new Uint8Array(e.target.result)
                const workbook = XLSX.read(data, { type: 'array' })
                
                // Read the Phase Hours by Week sheet
                const sheetName = workbook.SheetNames[0]
                const worksheet = workbook.Sheets[sheetName]
                const jsonData = XLSX.utils.sheet_to_json(worksheet)

                // Process and save the data
                const updates = []
                jsonData.forEach(row => {
                    const phaseCode = row['Phase']
                    if (!phaseCode) return

                    // Get all week columns (skip Phase, Description, Discipline)
                    Object.keys(row).forEach(key => {
                        if (key !== 'Phase' && key !== 'Description' && key !== 'Discipline') {
                            // Find the matching week from projectWeeks 
                            const matchingWeek = projectWeeks.find(week => 
                                moment(week).format('M/D/YY') === key
                            )
                            
                            if (matchingWeek && row[key] !== '') {
                                updates.push({
                                    userId: '154045',
                                    userName: 'Seth Bailey',
                                    fieldName: `week_${matchingWeek}`,
                                    tab: 'Phase Hours',
                                    rowId: phaseCode,
                                    version: selectedVersion + '',
                                    value: row[key] + '',
                                    id: selectedJob,
                                    lockedVersion: false
                                })
                            }
                        }
                    })
                })

                // Save all updates in one batch request
                if (updates.length > 0) {
                    await updateUserDataArray(updates)
                }

                // Reload the data
                const freshData = await loadUserData({ id: selectedJob, forecastGuid: selectedVersion })
                setJobUserData(freshData?.data)

                alert(`Successfully imported ${updates.length} values from Excel`)
                event.target.value = '' // Reset file input
            }
            reader.readAsArrayBuffer(file)
        } catch (error) {
            console.error('Error importing from Excel:', error)
            alert('Error importing from Excel. Please check the file format.')
        }
    }
    

    return (
        <View>
            <View style={a.graphOnly ? { padding: 10, paddingTop: 0, marginTop: 0 } : { padding: 30, paddingTop: 0, marginTop: 20 }}>
            <View ref={laborRef} style={{flexDirection: 'row', width: 'fit-content' }}>
                <View>
                    <View style={{ width: 'fit-content', position: 'relative' }}>
                        <View style={{ height: 500, minWidth: graphWidth + 220, width: graphWidth + 220, backgroundColor: 'white', borderRadius: 5, padding: 20, paddingLeft: 220, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 }}>
                            <Bar
                                plugins={[{
                                    id: 'trendingEACLabel',
                                    afterDraw(chart) {
                                        const dsIndex = chart.data.datasets.findIndex(d => d.label === 'Trending EAC (Productivity)');
                                        if (dsIndex === -1) return;
                                        const meta = chart.getDatasetMeta(dsIndex);
                                        const lastIdx = meta.data.length - 1;
                                        if (lastIdx < 0) return;
                                        const pt = meta.data[lastIdx];
                                        if (!pt || pt.skip) return;
                                        const ctx = chart.ctx;
                                        const x = pt.x;
                                        const y = pt.y;
                                        const val = chart.data.datasets[dsIndex].data[lastIdx];
                                        if (val == null) return;
                                        const label = 'Trending EAC Hours';
                                        const hours = Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
                                        const titleFont = 'bold 8px sans-serif';
                                        const bodyFont = '8px sans-serif';
                                        const boxH = 22;
                                        const padX = 4;
                                        const titleY = 3;
                                        const bodyY = 13;
                                        const padW = 8;
                                        ctx.save();
                                        ctx.font = titleFont;
                                        const labelWidth = Math.max(ctx.measureText(label).width, ctx.measureText(hours + ' hrs').width) + padW;
                                        const boxX = Math.min(x + 8, chart.width - labelWidth - 2);
                                        // Sit below the line endpoint so the box doesn't cover the trend line
                                        const offset = 6;
                                        let boxY = y + offset;
                                        if (boxY + boxH > chart.chartArea.bottom) {
                                            boxY = Math.max(chart.chartArea.top, y - boxH - offset);
                                        }
                                        ctx.fillStyle = '#f3f4f6';
                                        ctx.strokeStyle = '#9ca3af';
                                        ctx.lineWidth = 1;
                                        ctx.beginPath();
                                        ctx.rect(boxX, boxY, labelWidth, boxH);
                                        ctx.fill();
                                        ctx.stroke();
                                        ctx.fillStyle = '#374151';
                                        ctx.textAlign = 'left';
                                        ctx.textBaseline = 'top';
                                        ctx.fillText(label, boxX + padX, boxY + titleY);
                                        ctx.font = bodyFont;
                                        ctx.fillStyle = '#6b7280';
                                        ctx.fillText(hours + ' hrs', boxX + padX, boxY + bodyY);
                                        ctx.restore();
                                    }
                                }]}
                                data={{
                                    labels: projectWeeks.map((week, i) => `Week ${i + 1}`),
                                    datasets: [
                                        {
                                            label: 'Planned Weekly Hours',
                                            data: barData2.map(d => d.value),
                                            backgroundColor: colors.plannedCumulative,
                                            barThickness: 10,
                                            yAxisID: 'y',
                                            order: 2,
                                            barPercentage: .9,
                                            categoryPercentage: .9
                                        },
                                        {
                                            label: 'Forecasted Weekly Hours',
                                            data: barData3.map(d => d.value),
                                            backgroundColor: colors.forecastCumulative,
                                            barThickness: 10,
                                            yAxisID: 'y',
                                            order: 2,
                                            barPercentage: .9,
                                            categoryPercentage: .9
                                        },
                                        {
                                            label: 'Actual Weekly Hours',
                                            data: barData.map(d => d.value),
                                            backgroundColor: colors.actual,
                                            barThickness: 10,
                                            yAxisID: 'y',
                                            order: 2,
                                            barPercentage: .9,
                                            categoryPercentage: .9
                                        },
                                        {
                                            label: 'Future Forecasted Cumulative',
                                            data: lineData.futureForecastedCumulative.map(d => d.value),
                                            borderColor: colors.forecastCumulative,
                                            borderDash: [20, 20],
                                            borderWidth: 2,
                                            fill: false,
                                            tension: 0,
                                            yAxisID: 'y1',
                                            type: 'line',
                                            order: 2,
                                            pointRadius: 0,
                                            pointHoverRadius: 0
                                        },
                                        {
                                            label: 'Planned Cumulative',
                                            data: lineData.plannedCumulative.map(d => d.value),
                                            borderColor: colors.currentPlanned,
                                            borderWidth: 2,
                                            fill: false,
                                            tension: 0,
                                            yAxisID: 'y1',
                                            type: 'line',
                                            order: 1,
                                            pointRadius: 0,
                                            pointHoverRadius: 0
                                        },
                                        {
                                            label: 'Forecast Cumulative',
                                            data: lineData.forecastCumulative.map(d => d.value),
                                            borderColor: colors.plannedCumulative,
                                            borderWidth: 2,
                                            fill: false,
                                            tension: 0,
                                            yAxisID: 'y1',
                                            type: 'line',
                                            order: 1,
                                            pointRadius: 0,
                                            pointHoverRadius: 0
                                        },
                                        {
                                            label: 'Actual Cumulative',
                                            data: lineData.actualCumulative.map(d => d.value),
                                            borderColor: colors.actualCumulative,
                                            borderWidth: 2,
                                            fill: false,
                                            tension: 0,
                                            yAxisID: 'y1',
                                            type: 'line',
                                            order: 1,
                                            pointRadius: 0,
                                            pointHoverRadius: 0
                                        },
                                        ...(trendingEACTotal ? [{
                                            label: 'Trending EAC (Productivity)',
                                            data: projectWeeks.map((week, i) => {
                                                // Place dot on the last week
                                                return i === projectWeeks.length - 1 ? trendingEACTotal : null
                                            }),
                                            borderColor: colors.trendingEAC,
                                            backgroundColor: colors.trendingEAC,
                                            borderWidth: 0,
                                            fill: false,
                                            yAxisID: 'y1',
                                            type: 'line',
                                            order: 0,
                                            pointRadius: 4,
                                            pointHoverRadius: 5,
                                            pointStyle: 'circle',
                                            showLine: false,
                                            spanGaps: false
                                        }] : [])
                                    ]
                                }}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    layout: {
                                        padding: 0
                                    },
                                    scales: {
                                        x: {
                                            grid: {
                                                offset: false
                                            },
                                            ticks: {
                                                display: false
                                            },
                                        },
                                        y: {
                                            type: 'linear',
                                            position: 'left',
                                            beginAtZero: true,
                                            max: maxHeightBar + 200,
                                            afterFit: (scale) => {
                                                scale.width = 70; // pick what matches your table
                                            },
                                        },
                                        y1: {
                                            type: 'linear',
                                            position: 'right',
                                            beginAtZero: true,
                                            suggestedMax: maxValue,
                                            //max: maxValue,
                                            grid: {
                                                display: false
                                            },
                                            afterFit: (scale) => {
                                                scale.width = 70; // pick what matches your table
                                            },
                                        }
                                    },
                                    plugins: {
                                        legend: {
                                            display: false
                                        },
                                        tooltip: {
                                            titleFont: { size: 10, weight: 'bold' },
                                            bodyFont: { size: 9 },
                                            padding: 6,
                                            boxPadding: 3,
                                            boxWidth: 6,
                                            boxHeight: 6
                                        }
                                    }
                                }}
                            />
                        </View>
                    </View>
                    {/* The chart's legend: a square for a bar series, a short line for a line series */}
                    <View style={{ backgroundColor: 'white', borderRadius: 5, padding: '12px 16px', marginTop: 5, marginRight: 100, flexDirection: 'row', width: 'fit-content', shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 }}>
                            <View style={{ backgroundColor: 'white', width: 280 }}>
                                {[
                                    { label: 'Actual Weekly Hours', color: colors.actual, mark: 'bar' },
                                    { label: 'Planned Weekly Hours', color: colors.plannedCumulative, mark: 'bar' },
                                    { label: 'Forecasted Weekly Hours', color: colors.forecastCumulative, mark: 'bar' },
                                    { label: 'Current Planned Cumulative', color: colors.currentPlanned, mark: 'line' },
                                    { label: 'Planned Cumulative Hours', color: colors.plannedCumulative, mark: 'line' },
                                    { label: 'Actual Cumulative Hours', color: colors.actualCumulative, mark: 'line' },
                                    { label: 'Forecasted Cumulative Hours', color: colors.forecastCumulative, mark: 'line' },
                                ].map((item) => (
                                    <View key={item.label} style={{ flexDirection: 'row', alignItems: 'center', height: 22 }}>
                                        <View style={{ width: 18, alignItems: 'center', marginRight: 10 }}>
                                            {item.mark === 'bar'
                                                ? <View style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: item.color }}></View>
                                                : <View style={{ width: 18, height: 2, backgroundColor: item.color }}></View>}
                                        </View>
                                        <Text style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>{item.label}</Text>
                                    </View>
                                ))}
                                {trendingEACTotal ? (
                                    <View style={{ flexDirection: 'row', alignItems: 'center', height: 22 }}>
                                        <View style={{ width: 18, alignItems: 'center', marginRight: 10 }}>
                                            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: colors.trendingEAC }}></View>
                                        </View>
                                        <Text style={{ fontSize: 12, fontWeight: 500, flex: 1 }}>
                                            Trending EAC Hours ({fNum(trendingEACTotal)})
                                        </Text>
                                    </View>
                                ) : null}
                            </View>
                            {!a.graphOnly && historicalWeeks.map((row, index) => (
                                <HistoricalRow key={index} row={row} index={index} />))
                            }
                    </View>
                </View>
            </View>
            {/* the hours table: the card is as wide as its widest row so no cell shrinks (header and week rows agree) */}
            {(a.photo || a.graphOnly) ? null :<View style={{ width: 'max-content', backgroundColor: 'white', borderRadius: 5, padding: 20, marginTop: 20, marginBottom: 100, marginRight: 100, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3 }}>
                <View style={{ flexDirection: 'row' }}>
                    <Text style={s(360).title}>Bid Budget/CO Hour Budget</Text>
                    <Text style={s(120).title}>{totalDayZero ? fNum(totalDayZero) : ''}</Text>
                    <Text style={s(120).title}>{totalCO ? fNum(totalCO) : ''}</Text>
                    <View style={{width: 10}}></View>
                    <Text style={s(270).title}>Planned Cumulative = Original Hours</Text>
                    <Text style={[s(120).title, { backgroundColor: validCell(cumulatives[projectWeeks[projectWeeks.length - 1]]?.planned, totalDayZero) }]}>
                        {fNum(cumulatives[projectWeeks[projectWeeks.length - 1]]?.planned)}
                    </Text>
                    <Text style={[s(120).title, { backgroundColor: validCell(totalDayZero, cumulatives[projectWeeks[projectWeeks.length - 1]]?.planned) }]}>
                        {totalDayZero ? fNum(pNum(totalDayZero)) : ''}
                    </Text>
                </View>
                <View style={{ flexDirection: 'row' }}>
                    <Text style={s(360).title}>Remaining</Text>
                    <Text style={s(120).title}>{totalDayZero - cumulatives[projectWeeks[projectWeeks.length - 1]]?.planned ? fNum(totalDayZero - cumulatives[projectWeeks[projectWeeks.length - 1]]?.planned) : ''}</Text>
                    <Text style={s(120).title}>{totalCO - cumulatives[projectWeeks[projectWeeks.length - 1]]?.currentPlanned ? fNum(totalCO - cumulatives[projectWeeks[projectWeeks.length - 1]]?.currentPlanned + cumulatives[projectWeeks[projectWeeks.length - 1]]?.planned) : ''}</Text>
                    <View style={{width: 10}}></View>
                    <Text style={s(270).title}>Current Planned Cumul. = Current Budget</Text>
                    <Text style={[s(120).title, { backgroundColor: validCell(cumulatives[projectWeeks[projectWeeks.length - 1]]?.currentPlanned, pNum(totalDayZero) + pNum(totalCO)) }]}>
                        {fNum(cumulatives[projectWeeks[projectWeeks.length - 1]]?.currentPlanned)}
                    </Text>
                    <Text style={[s(120).title, { backgroundColor: validCell(pNum(totalDayZero) + pNum(totalCO), cumulatives[projectWeeks[projectWeeks.length - 1]]?.currentPlanned) }]}>
                        {fNum(pNum(totalDayZero) + pNum(totalCO))}
                    </Text>
                </View>
                <View style={{ flexDirection: 'row' }}>
                    <Text style={s(120).title}>Job Week</Text>
                    <Text style={s(120).title}>Week (starting Monday)</Text>
                    <Text style={s(120).title}>Actual Weekly Hours</Text>
                    <Text style={s(120).title}>Planned Weekly Hours</Text>
                    <Text style={s(120).title}>Approved CO Hours</Text>
                    <Text style={s(120).title}>Forecasted Weekly Hours</Text>
                    <View style={{ width: 20 }}></View>
                    <Text style={s(120).title}>Actual Cumulative Hours</Text>
                    <Text style={s(120).title}>Planned Cumulative Hours</Text>
                    <Text style={s(120).title}>Current Planned Cumulative Hours</Text>
                    <Text style={s(120).title}>Forecasted Cumulative Hours</Text>
                    {/* <View style={{ width: 20 }}></View>
                    <Text style={s(120).title}>Actual Staff Count</Text>
                    <Text style={s(120).title}>Planned Staff Count</Text>
                    <Text style={s(120).title}>Forecasted Staff Count</Text> */}
                </View>
                {projectWeeks.map((week, i) => (
                    <View key={i}>
                        <LaborWeek i={i} week={week} />
                    </View>
                ))}
            </View>}

            {/* Phase Hours Table */}
            {(a.photo || a.graphOnly) ? null : <View style={{ backgroundColor: 'white', borderRadius: 5, padding: 15, marginTop: 20, marginBottom: 20, marginRight: 100, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, elevation: 3, width: 'max-content', minWidth: 700 }}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <View style={{ flexDirection: 'row', gap: 10 }}>
                        <Pressable 
                            onPress={exportToExcel}
                            style={{ 
                                backgroundColor: '#f8fafc', 
                                padding: 12, 
                                paddingHorizontal: 16, 
                                borderRadius: 6, 
                                border: '1px solid #cbd5e1',
                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            <Text style={{ color: '#334155', fontSize: 14, fontWeight: '500' }}>Export to Excel</Text>
                        </Pressable>
                        <label style={{ 
                            backgroundColor: '#f8fafc', 
                            padding: 12, 
                            paddingHorizontal: 16, 
                            borderRadius: 6, 
                            cursor: 'pointer',
                            border: '1px solid #cbd5e1',
                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            <Text style={{ color: '#334155', fontSize: 14, fontWeight: '500' }}>Import from Excel</Text>
                            <input
                                type="file"
                                accept=".xlsx, .xls"
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                            />
                        </label>
                    </View>
                    <Text style={{ fontSize: 14, fontWeight: 'bold' }}>Budget Phase Hours by Week</Text>
                </View>
                <PhaseHoursTable
                    sections={sections}
                    jobCostDetail={jobCostDetail}
                    userData={userData}
                    projectWeeks={projectWeeks}
                    selectedJob={selectedJob}
                    selectedVersion={selectedVersion}
                    setJobUserData={setJobUserData}
                    onInputFocus={() => { focusedId.current = null }}
                />
            </View>}

            <View style={{ height: 300 }}></View>
            </View>
        </View>
    );
};

// Budget Phase Hours / Quantities by Week (the manpower plan). These live at
// module level so a LaborGraphs re-render doesn't remount the inputs — a
// remount drops whatever the user typed back to the last loaded value.
const phaseCellKey = (tab, rowId, week) => tab + '|' + rowId + '|' + week

const PhaseRow = ({ phase, description, rowId, userData, projectWeeks, actualHoursByPhaseWeek, forecastHoursByPhaseWeek, pendingEdits, queueEdit, commitCell, onInputFocus }) => {
    const [phaseHours, setPhaseHours] = useState({})
    
    // Calculate total from day zero budget and change orders
    const calculateTotal = () => {
        let dayZeroHours = 0
        let coHours = 0
        
        // Get day zero budget hours for this cost code
        if (userData?.dayZero) {
            Object.values(userData.dayZero).forEach(item => {
                if (item?.Cost_Code == rowId) {
                    dayZeroHours += pNum(item?.Hours || 0)
                }
            })
        }
        
        // Get approved CO hours for this cost code
        if (userData?.cos) {
            Object.values(userData.cos).forEach(co => {
                if ((co?.Cost_Code == rowId) && (co?.Status === 'Approved')) {
                    coHours += pNum(co?.Hours || 0)
                }
            })
        }
        
        return dayZeroHours + coHours
    }

    // Calculate remaining (total - sum of weekly hours)
    const calculateRemaining = () => {
        const total = calculateTotal()
        let usedHours = 0
        projectWeeks.forEach(week => {
            usedHours += pNum(userData?.phaseHours?.[rowId]?.weeks?.[week] || 0)
        })
        return total - usedHours
    }

    const totalHours = calculateTotal()
    const remaining = calculateRemaining()

    useEffect(() => {
        const hours = {}
        projectWeeks.forEach(week => {
            // a cell still waiting to save keeps what was typed
            const pending = pendingEdits.current[phaseCellKey('Phase Hours', rowId, week)]
            hours[week] = pending ? pending.value : (userData?.phaseHours?.[rowId]?.weeks?.[week] || '')
        })
        setPhaseHours(hours)
    }, [userData, rowId, projectWeeks])

    const handleWeekChange = (week, value) => {
        setPhaseHours(prev => ({ ...prev, [week]: value }))
        queueEdit('Phase Hours', rowId, week, value)
    }

    const actualTotal = Object.values(actualHoursByPhaseWeek?.[rowId] || {}).reduce((a, b) => a + b, 0)
    const forecastTotal = Object.values(forecastHoursByPhaseWeek?.[rowId] || {}).reduce((a, b) => a + b, 0)

    return (
        <>
            <View style={{ flexDirection: 'row' }}>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 11, borderRight: '1px solid #aeadaf' }]}>{phase}</Text>
                <Text style={[s(150).title, { borderTop: 0, fontSize: 11, borderRight: '1px solid #aeadaf', textAlign: 'left' }]}>{description}</Text>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 11, textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf' }]}>
                    {totalHours > 0 ? fNum(totalHours,1) : ''}
                </Text>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 11, textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf' }]}>
                    {remaining !== 0 ? fNum(remaining,1) : ''}
                </Text>
                {projectWeeks.map((week, j) => (
                    <TextInput
                        key={j}
                        style={[s(60).rate, { borderTop: 0, fontSize: 11, textAlign: 'right', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, maxWidth: 60 }]}
                        value={phaseHours[week] || ''}
                        onChangeText={(value) => handleWeekChange(week, value)}
                        onFocus={onInputFocus}
                        onBlur={() => commitCell('Phase Hours', rowId, week)}
                    />
                ))}
            </View>
            <View style={{ flexDirection: 'row', backgroundColor: '#fff8e1' }}>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 10, borderRight: '1px solid #aeadaf' }]}></Text>
                <Text style={[s(150).title, { borderTop: 0, fontSize: 10, borderRight: '1px solid #aeadaf', textAlign: 'left', fontStyle: 'italic', color: '#795548' }]}>Actual</Text>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 10, textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf', color: '#795548' }]}>
                    {actualTotal > 0 ? fNum(actualTotal, 1) : ''}
                </Text>
                <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                {projectWeeks.map((week, j) => {
                    const val = actualHoursByPhaseWeek?.[rowId]?.[week] || 0
                    return (
                        <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 10, textAlign: 'right', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, color: '#795548' }]}>
                            {val > 0 ? fNum(val, 1) : ''}
                        </Text>
                    )
                })}
            </View>
            <View style={{ flexDirection: 'row', backgroundColor: '#e3f2fd' }}>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 10, borderRight: '1px solid #aeadaf' }]}></Text>
                <Text style={[s(150).title, { borderTop: 0, fontSize: 10, borderRight: '1px solid #aeadaf', textAlign: 'left', fontStyle: 'italic', color: '#1565c0' }]}>Forecast</Text>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 10, textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf', color: '#1565c0' }]}>
                    {forecastTotal > 0 ? fNum(forecastTotal, 1) : ''}
                </Text>
                <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                {projectWeeks.map((week, j) => {
                    const val = forecastHoursByPhaseWeek?.[rowId]?.[week] || 0
                    return (
                        <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 10, textAlign: 'right', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, color: '#1565c0' }]}>
                            {val > 0 ? fNum(val, 1) : ''}
                        </Text>
                    )
                })}
            </View>
        </>
    )
}

const QuantityPhaseRow = ({ phase, description, rowId, userData, projectWeeks, actualQtyByPhaseWeek, pendingEdits, queueEdit, commitCell, onInputFocus }) => {
    const [phaseQty, setPhaseQty] = useState({})

    // Calculate total quantity from day zero budget + approved COs
    const calculateQtyTotal = () => {
        let dzQty = 0, coQty = 0
        if (userData?.dayZero) Object.values(userData.dayZero).forEach(item => { if (item?.Cost_Code == rowId) dzQty += pNum(item?.Quantity || 0) })
        if (userData?.cos) Object.values(userData.cos).forEach(co => { if ((co?.Cost_Code == rowId) && (co?.Status === 'Approved')) coQty += pNum(co?.Quantity || 0) })
        return dzQty + coQty
    }

    const calculateQtyRemaining = () => {
        const total = calculateQtyTotal()
        let used = 0
        projectWeeks.forEach(week => { used += pNum(userData?.phaseQuantities?.[rowId]?.weeks?.[week] || 0) })
        return total - used
    }

    const qtyTotal = calculateQtyTotal()
    const qtyRemaining = calculateQtyRemaining()

    useEffect(() => {
        const qty = {}
        projectWeeks.forEach(week => {
            const pending = pendingEdits.current[phaseCellKey('Phase Quantities', rowId, week)]
            qty[week] = pending ? pending.value : (userData?.phaseQuantities?.[rowId]?.weeks?.[week] || '')
        })
        setPhaseQty(qty)
    }, [userData, rowId, projectWeeks])

    const handleQtyWeekChange = (week, value) => {
        setPhaseQty(prev => ({ ...prev, [week]: value }))
        queueEdit('Phase Quantities', rowId, week, value)
    }

    const actualQtyTotal = Object.values(actualQtyByPhaseWeek?.[rowId] || {}).reduce((a, b) => a + b, 0)

    return (
        <>
            {/* Plan row */}
            <View style={{ flexDirection: 'row' }}>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 11, borderRight: '1px solid #aeadaf' }]}>{phase}</Text>
                <Text style={[s(150).title, { borderTop: 0, fontSize: 11, borderRight: '1px solid #aeadaf', textAlign: 'left' }]}>{description}</Text>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 11, textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf' }]}>
                    {qtyTotal > 0 ? fNum(qtyTotal, 1) : ''}
                </Text>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 11, textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf' }]}>
                    {qtyRemaining !== 0 ? fNum(qtyRemaining, 1) : ''}
                </Text>
                {projectWeeks.map((week, j) => (
                    <TextInput
                        key={j}
                        style={[s(60).rate, { borderTop: 0, fontSize: 11, textAlign: 'right', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, maxWidth: 60 }]}
                        value={phaseQty[week] || ''}
                        onChangeText={(value) => handleQtyWeekChange(week, value)}
                        onFocus={onInputFocus}
                        onBlur={() => commitCell('Phase Quantities', rowId, week)}
                    />
                ))}
            </View>
            {/* Actual row */}
            <View style={{ flexDirection: 'row', backgroundColor: '#fff8e1' }}>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 10, borderRight: '1px solid #aeadaf' }]}></Text>
                <Text style={[s(150).title, { borderTop: 0, fontSize: 10, borderRight: '1px solid #aeadaf', textAlign: 'left', fontStyle: 'italic', color: '#795548' }]}>Actual</Text>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 10, textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf', color: '#795548' }]}>
                    {actualQtyTotal > 0 ? fNum(actualQtyTotal, 1) : ''}
                </Text>
                <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                {projectWeeks.map((week, j) => {
                    const val = actualQtyByPhaseWeek?.[rowId]?.[week] || 0
                    return (
                        <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 10, textAlign: 'right', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, color: '#795548' }]}>
                            {val > 0 ? fNum(val, 1) : ''}
                        </Text>
                    )
                })}
            </View>
            {/* Forecast row */}
            <View style={{ flexDirection: 'row', backgroundColor: '#e3f2fd' }}>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 10, borderRight: '1px solid #aeadaf' }]}></Text>
                <Text style={[s(150).title, { borderTop: 0, fontSize: 10, borderRight: '1px solid #aeadaf', textAlign: 'left', fontStyle: 'italic', color: '#1565c0' }]}>Forecast</Text>
                <Text style={[s(70).title, { borderTop: 0, fontSize: 10, textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf', color: '#1565c0' }]}></Text>
                <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                {projectWeeks.map((week, j) => (
                    <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 10, textAlign: 'right', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, color: '#1565c0' }]}>
                    </Text>
                ))}
            </View>
        </>
    )
}

function PhaseHoursTable({ sections, jobCostDetail, userData, projectWeeks, selectedJob, selectedVersion, setJobUserData, onInputFocus }) {
    const [expandedSections, setExpandedSections] = useState({})
    const [quantitiesCollapsed, setQuantitiesCollapsed] = useState(true)
    const [percentageCollapsed, setPercentageCollapsed] = useState(true)
    const initializedRef = useRef(false)
    // Typed-but-unsaved cells, keyed by phaseCellKey. Each cell debounces on
    // its own timer so typing in the next week doesn't cancel the last save.
    const pendingEdits = useRef({})
    // Saves go to the server one at a time — two cells landing together can
    // collide on the insert and one of them comes back 500.
    const saveChain = useRef(Promise.resolve())

    const commitEdit = useCallback((key) => {
        const edit = pendingEdits.current[key]
        if (!edit) return
        clearTimeout(edit.timer)
        delete pendingEdits.current[key]
        const fieldName = `week_${edit.week}`
        saveChain.current = saveChain.current.then(() => updateUserDataArray({
            userId: '154045',
            userName: 'Seth Bailey',
            fieldName: fieldName,
            tab: edit.tab,
            rowId: edit.rowId,
            version: edit.version + '',
            value: edit.value + '',
            id: edit.job,
            lockedVersion: false
        })).catch((e) => console.error('Phase plan save failed', e))
        // Write the value into the loaded user data too, so totals, the
        // export and the other tabs see it without a reload.
        setJobUserData((prev) => {
            const rows = Array.isArray(prev) ? prev : []
            if (rows.length && rows[0]?.BU_ID && rows[0].BU_ID != edit.job) return prev
            let found = false
            const next = rows.map((d) => {
                if (d?.Forecast_Tab === edit.tab && d?.Forecast_Row_ID == edit.rowId && d?.Forecast_Field_Name === fieldName && (!d?.Version_GUID || d.Version_GUID == edit.version)) {
                    found = true
                    return { ...d, Forecast_Value: edit.value }
                }
                return d
            })
            if (!found) {
                next.push({
                    Forecast_Tab: edit.tab,
                    Forecast_Row_ID: edit.rowId,
                    Forecast_Field_Name: fieldName,
                    Forecast_Value: edit.value,
                    BU_ID: edit.job,
                    Version_GUID: edit.version
                })
            }
            return next
        })
    }, [setJobUserData])

    const queueEdit = useCallback((tab, rowId, week, value) => {
        if (!selectedJob || value === 'undefined') return
        const key = phaseCellKey(tab, rowId, week)
        clearTimeout(pendingEdits.current[key]?.timer)
        pendingEdits.current[key] = {
            tab, rowId, week, value,
            job: selectedJob,
            version: selectedVersion,
            timer: setTimeout(() => commitEdit(key), 1000)
        }
    }, [selectedJob, selectedVersion, commitEdit])

    const commitCell = useCallback((tab, rowId, week) => commitEdit(phaseCellKey(tab, rowId, week)), [commitEdit])

    // Leaving the page or switching job/version saves anything still waiting
    useEffect(() => {
        return () => { Object.keys(pendingEdits.current).forEach(commitEdit) }
    }, [selectedJob, selectedVersion, commitEdit])

    // Initialize all sections as expanded (only once)
    useEffect(() => {
        if (sections.length > 0 && !initializedRef.current) {
            const initial = {}
            sections.forEach((_, i) => {
                initial[i] = true
            })
            setExpandedSections(initial)
            initializedRef.current = true
        }
    }, [sections])

    // Compute actual hours by phase by week from job cost detail
    const actualHoursByPhaseWeek = useMemo(() => {
        const result = {}
        jobCostDetail?.forEach(row => {
            if (row.Cost_Type_Code >= '51000' && row.Cost_Type_Code <= '51799' && row.Phase_Code) {
                const week = moment(row.Consolidated_Date).startOf('isoWeek').format('YYYY-MM-DD')
                if (!result[row.Phase_Code]) result[row.Phase_Code] = {}
                result[row.Phase_Code][week] = (result[row.Phase_Code][week] || 0) + pNum(row.Total_Hours)
            }
        })
        return result
    }, [jobCostDetail])

    // Compute forecast hours by phase by week from phased burn tool
    const forecastHoursByPhaseWeek = useMemo(() => {
        const result = {}
        if (userData?.burnTool) {
            Object.keys(userData.burnTool).forEach(key => {
                if (key.includes('=')) {
                    const phase = key.split('=')[0]
                    const week = key.split('=')[1]
                    const bt = userData.burnTool[key]
                    const h = pNum(bt?.Number_Hours || 0)
                    const cs = pNum(bt?.Number_Units || 0)
                    if (h && cs) {
                        if (!result[phase]) result[phase] = {}
                        result[phase][week] = (result[phase][week] || 0) + (h * cs)
                    }
                }
            })
        }
        return result
    }, [userData])

    // Compute actual quantities by phase by week (FP document type = installed quantities)
    const actualQtyByPhaseWeek = useMemo(() => {
        const result = {}
        jobCostDetail?.forEach(row => {
            if (row.Document_Type === 'FP' && row.Phase_Code) {
                const week = moment(row.Consolidated_Date).startOf('isoWeek').format('YYYY-MM-DD')
                if (!result[row.Phase_Code]) result[row.Phase_Code] = {}
                result[row.Phase_Code][week] = (result[row.Phase_Code][week] || 0) + pNum(row.Total_Hours)
            }
        })
        return result
    }, [jobCostDetail])

    // Helper: actual hours total for a week across all phases
    const calculateWeekActualTotal = (week) => {
        let total = 0
        sections.forEach(sp => sp.forEach(p => { total += (actualHoursByPhaseWeek?.[p.code]?.[week] || 0) }))
        return total
    }

    // Helper: forecast hours total for a week across all phases
    const calculateWeekForecastTotal = (week) => {
        let total = 0
        sections.forEach(sp => sp.forEach(p => { total += (forecastHoursByPhaseWeek?.[p.code]?.[week] || 0) }))
        return total
    }

    // Helper: actual quantity total for a week across all phases
    const calculateWeekActualQtyTotal = (week) => {
        let total = 0
        sections.forEach(sp => sp.forEach(p => { total += (actualQtyByPhaseWeek?.[p.code]?.[week] || 0) }))
        return total
    }

    // Helper: planned quantity total for a week across all phases
    const calculateWeekPlannedQtyTotal = (week) => {
        let total = 0
        sections.forEach(sp => sp.forEach(p => { total += pNum(userData?.phaseQuantities?.[p.code]?.weeks?.[week] || 0) }))
        return total
    }

    // Helper: grand total for quantities budget (DZ Quantity + approved CO Quantity)
    const calculateQtyGrandTotal = () => {
        let total = 0
        sections.forEach(sp => sp.forEach(phase => {
            if (userData?.dayZero) {
                Object.values(userData.dayZero).forEach(item => {
                    if (item?.Cost_Code == phase.code) total += pNum(item?.Quantity || 0)
                })
            }
            if (userData?.cos) {
                Object.values(userData.cos).forEach(co => {
                    if ((co?.Cost_Code == phase.code) && (co?.Status === 'Approved')) total += pNum(co?.Quantity || 0)
                })
            }
        }))
        return total
    }

    const DisciplineRow = ({ sectionPhases, disciplineName, sectionIndex }) => {
        const isExpanded = expandedSections[sectionIndex] !== false

        // Calculate discipline totals
        const calculateDisciplineTotal = () => {
            return sectionPhases.reduce((sum, phase) => {
                let dayZeroHours = 0
                let coHours = 0
                
                if (userData?.dayZero) {
                    Object.values(userData.dayZero).forEach(item => {
                        if (item?.Cost_Code == phase.code) {
                            dayZeroHours += pNum(item?.Hours || 0)
                        }
                    })
                }
                
                if (userData?.cos) {
                    Object.values(userData.cos).forEach(co => {
                        if ((co?.Cost_Code == phase.code) && (co?.Status === 'Approved')) {
                            coHours += pNum(co?.Hours || 0)
                        }
                    })
                }
                
                return sum + dayZeroHours + coHours
            }, 0)
        }

        const calculateDisciplineRemaining = () => {
            const total = calculateDisciplineTotal()
            let usedHours = 0
            sectionPhases.forEach(phase => {
                projectWeeks.forEach(week => {
                    usedHours += pNum(userData?.phaseHours?.[phase.code]?.weeks?.[week] || 0)
                })
            })
            return total - usedHours
        }

        const calculateDisciplineWeekHours = (week) => {
            return sectionPhases.reduce((sum, phase) => {
                return sum + pNum(userData?.phaseHours?.[phase.code]?.weeks?.[week] || 0)
            }, 0)
        }

        return (
            <View style={{ flexDirection: 'row', backgroundColor: '#efefef' }}>
                <Pressable onPress={() => setExpandedSections(prev => ({ ...prev, [sectionIndex]: !isExpanded }))} style={{ flexDirection: 'row', flex: 1 }}>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}>{isExpanded ? '▼' : '►'}</Text>
                    <Text style={[s(150).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf', textAlign: 'left' }]}>{disciplineName}</Text>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf' }]}>
                        {fNum(calculateDisciplineTotal(), 1)}
                    </Text>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf' }]}>
                        {calculateDisciplineRemaining() !== 0 ? fNum(calculateDisciplineRemaining(), 1) : ''}
                    </Text>
                    {projectWeeks.map((week, j) => {
                        const weekHours = calculateDisciplineWeekHours(week)
                        return (
                            <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', textAlign: 'right', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                                {weekHours > 0 ? fNum(weekHours, 1) : ''}
                            </Text>
                        )
                    })}
                </Pressable>
            </View>
        )
    }

    // Calculate totals across all phases
    const calculateWeekTotal = (week) => {
        let total = 0
        sections.forEach(sectionPhases => {
            sectionPhases.forEach(phase => {
                total += pNum(userData?.phaseHours?.[phase.code]?.weeks?.[week] || 0)
            })
        })
        return total
    }

    const calculateGrandTotal = () => {
        let total = 0
        sections.forEach(sectionPhases => {
            sectionPhases.forEach(phase => {
                let dayZeroHours = 0
                let coHours = 0
                
                if (userData?.dayZero) {
                    Object.values(userData.dayZero).forEach(item => {
                        if (item?.Cost_Code == phase.code) {
                            dayZeroHours += pNum(item?.Hours || 0)
                        }
                    })
                }
                
                if (userData?.cos) {
                    Object.values(userData.cos).forEach(co => {
                        if ((co?.Cost_Code == phase.code) && (co?.Status === 'Approved')) {
                            coHours += pNum(co?.Hours || 0)
                        }
                    })
                }
                
                total += dayZeroHours + coHours
            })
        })
        return total
    }

    return (
        /* As wide as the widest week row, so no cell shrinks and the header and the
           Actual / Forecast rows agree; the card around it grows to match. */
        <View style={{ width: 'max-content', minWidth: '100%' }}>
            {/* Hours Table */}
            <View style={{ marginBottom: 20 }}>
                <View style={{ flexDirection: 'row' }}>
                    <Text style={[s(70).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Phase</Text>
                    <Text style={[s(150).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Description</Text>
                    <Text style={[s(70).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Total</Text>
                    <Text style={[s(70).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Remain</Text>
                    {projectWeeks.map((week, i) => (
                        <Text key={i} style={[s(60).title, { fontSize: 10, borderRight: i === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                            {moment(week).format('M/D/YY')}
                        </Text>
                    ))}
                </View>
                
                {sections.map((sectionPhases, i) => (
                    <View key={i}>
                        <DisciplineRow sectionPhases={sectionPhases} disciplineName={sectionPhases[0]?.discipline || 'Other'} sectionIndex={i} />
                        {expandedSections[i] !== false && sectionPhases.map((phase, j) => (
                            <PhaseRow key={phase.code} phase={phase.code} description={phase.description} rowId={phase.code} userData={userData} projectWeeks={projectWeeks} actualHoursByPhaseWeek={actualHoursByPhaseWeek} forecastHoursByPhaseWeek={forecastHoursByPhaseWeek} pendingEdits={pendingEdits} queueEdit={queueEdit} commitCell={commitCell} onInputFocus={onInputFocus} />
                        ))}
                    </View>
                ))}
                
                {/* Total Row - Plan */}
                <View style={{ flexDirection: 'row' }}>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}>Total</Text>
                    <Text style={[s(150).title, { borderTop: 0, borderRight: '1px solid #aeadaf', fontSize: 11, fontWeight: 'bold' }]}>Plan</Text>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}>
                        {fNum(calculateGrandTotal())}
                    </Text>
                    <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                    {projectWeeks.map((week, j) => {
                        const total = calculateWeekTotal(week)
                        return (
                            <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                                {total > 0 ? fNum(total) : ''}
                            </Text>
                        )
                    })}
                </View>
                {/* Total Row - Actual */}
                <View style={{ flexDirection: 'row', backgroundColor: '#fff8e1' }}>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}></Text>
                    <Text style={[s(150).title, { borderTop: 0, borderRight: '1px solid #aeadaf', fontSize: 11, fontWeight: 'bold', fontStyle: 'italic', color: '#795548' }]}>Actual</Text>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf', color: '#795548' }]}>
                        {(() => { let t = 0; projectWeeks.forEach(w => t += calculateWeekActualTotal(w)); return t > 0 ? fNum(t) : ''; })()}
                    </Text>
                    <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                    {projectWeeks.map((week, j) => {
                        const total = calculateWeekActualTotal(week)
                        return (
                            <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, color: '#795548' }]}>
                                {total > 0 ? fNum(total) : ''}
                            </Text>
                        )
                    })}
                </View>
                {/* Total Row - Forecast */}
                <View style={{ flexDirection: 'row', backgroundColor: '#e3f2fd' }}>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}></Text>
                    <Text style={[s(150).title, { borderTop: 0, borderRight: '1px solid #aeadaf', fontSize: 11, fontWeight: 'bold', fontStyle: 'italic', color: '#1565c0' }]}>Forecast</Text>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf', color: '#1565c0' }]}>
                        {(() => { let t = 0; projectWeeks.forEach(w => t += calculateWeekForecastTotal(w)); return t > 0 ? fNum(t) : ''; })()}
                    </Text>
                    <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                    {projectWeeks.map((week, j) => {
                        const total = calculateWeekForecastTotal(week)
                        return (
                            <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, color: '#1565c0' }]}>
                                {total > 0 ? fNum(total) : ''}
                            </Text>
                        )
                    })}
                </View>
            </View>

            {/* Quantities Table */}
            <View style={{ marginBottom: 20 }}>
                <Pressable onPress={() => setQuantitiesCollapsed(!quantitiesCollapsed)} style={{ flexDirection: 'row', alignItems: 'center', marginTop: 20, marginBottom: 8 }}>
                    <Text style={{ fontSize: 14, fontWeight: 'bold' }}>{quantitiesCollapsed ? '►' : '▼'} Budget Phase Quantities by Week</Text>
                </Pressable>
                {!quantitiesCollapsed && <>
                <View style={{ flexDirection: 'row' }}>
                    <Text style={[s(70).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Phase</Text>
                    <Text style={[s(150).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Description</Text>
                    <Text style={[s(70).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Total</Text>
                    <Text style={[s(70).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Remain</Text>
                    {projectWeeks.map((week, i) => (
                        <Text key={i} style={[s(60).title, { fontSize: 10, borderRight: i === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                            {moment(week).format('M/D/YY')}
                        </Text>
                    ))}
                </View>

                {sections.map((sectionPhases, i) => {
                    // Discipline Quantity header
                    const isExpanded = expandedSections[i] !== false
                    const calcDisciplineQtyTotal = () => {
                        return sectionPhases.reduce((sum, phase) => {
                            let dzQty = 0, coQty = 0
                            if (userData?.dayZero) Object.values(userData.dayZero).forEach(item => { if (item?.Cost_Code == phase.code) dzQty += pNum(item?.Quantity || 0) })
                            if (userData?.cos) Object.values(userData.cos).forEach(co => { if ((co?.Cost_Code == phase.code) && (co?.Status === 'Approved')) coQty += pNum(co?.Quantity || 0) })
                            return sum + dzQty + coQty
                        }, 0)
                    }
                    const calcDisciplineQtyRemaining = () => {
                        const total = calcDisciplineQtyTotal()
                        let used = 0
                        sectionPhases.forEach(phase => { projectWeeks.forEach(week => { used += pNum(userData?.phaseQuantities?.[phase.code]?.weeks?.[week] || 0) }) })
                        return total - used
                    }
                    const calcDisciplineQtyWeek = (week) => {
                        return sectionPhases.reduce((sum, phase) => sum + pNum(userData?.phaseQuantities?.[phase.code]?.weeks?.[week] || 0), 0)
                    }
                    const calcDisciplineActualQtyWeek = (week) => {
                        return sectionPhases.reduce((sum, phase) => sum + (actualQtyByPhaseWeek?.[phase.code]?.[week] || 0), 0)
                    }

                    return (
                        <View key={i}>
                            {/* Discipline header - Planned Qty */}
                            <View style={{ flexDirection: 'row', backgroundColor: '#efefef' }}>
                                <Pressable onPress={() => setExpandedSections(prev => ({ ...prev, [i]: !isExpanded }))} style={{ flexDirection: 'row', flex: 1 }}>
                                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}>{isExpanded ? '▼' : '►'}</Text>
                                    <Text style={[s(150).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf', textAlign: 'left' }]}>{sectionPhases[0]?.discipline || 'Other'}</Text>
                                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf' }]}>
                                        {fNum(calcDisciplineQtyTotal(), 1)}
                                    </Text>
                                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', textAlign: 'right', padding: 4, borderRight: '1px solid #aeadaf' }]}>
                                        {calcDisciplineQtyRemaining() !== 0 ? fNum(calcDisciplineQtyRemaining(), 1) : ''}
                                    </Text>
                                    {projectWeeks.map((week, j) => {
                                        const wQty = calcDisciplineQtyWeek(week)
                                        return (
                                            <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', textAlign: 'right', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                                                {wQty > 0 ? fNum(wQty, 1) : ''}
                                            </Text>
                                        )
                                    })}
                                </Pressable>
                            </View>
                            {/* Expanded phase quantity rows */}
                            {isExpanded && sectionPhases.map((phase, j) => (
                                <QuantityPhaseRow key={phase.code} phase={phase.code} description={phase.description} rowId={phase.code} userData={userData} projectWeeks={projectWeeks} actualQtyByPhaseWeek={actualQtyByPhaseWeek} pendingEdits={pendingEdits} queueEdit={queueEdit} commitCell={commitCell} onInputFocus={onInputFocus} />
                            ))}
                        </View>
                    )
                })}

                {/* Quantity Total Row - Plan */}
                <View style={{ flexDirection: 'row' }}>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}>Total</Text>
                    <Text style={[s(150).title, { borderTop: 0, borderRight: '1px solid #aeadaf', fontSize: 11, fontWeight: 'bold' }]}>Plan</Text>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}>
                        {fNum(calculateQtyGrandTotal())}
                    </Text>
                    <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                    {projectWeeks.map((week, j) => {
                        const total = calculateWeekPlannedQtyTotal(week)
                        return (
                            <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                                {total > 0 ? fNum(total) : ''}
                            </Text>
                        )
                    })}
                </View>
                {/* Quantity Total Row - Actual */}
                <View style={{ flexDirection: 'row', backgroundColor: '#fff8e1' }}>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}></Text>
                    <Text style={[s(150).title, { borderTop: 0, borderRight: '1px solid #aeadaf', fontSize: 11, fontWeight: 'bold', fontStyle: 'italic', color: '#795548' }]}>Actual</Text>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf', color: '#795548' }]}>
                        {(() => { let t = 0; projectWeeks.forEach(w => t += calculateWeekActualQtyTotal(w)); return t > 0 ? fNum(t) : ''; })()}
                    </Text>
                    <Text style={[s(70).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                    {projectWeeks.map((week, j) => {
                        const total = calculateWeekActualQtyTotal(week)
                        return (
                            <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0, color: '#795548' }]}>
                                {total > 0 ? fNum(total) : ''}
                            </Text>
                        )
                    })}
                </View>
                </>}
            </View>

            {/* Percentage Table */}
            <View>
                <Pressable onPress={() => setPercentageCollapsed(!percentageCollapsed)} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
                    <Text style={{ fontSize: 12, fontWeight: 'bold' }}>{percentageCollapsed ? '►' : '▼'} Budget Phase Percentage Distribution</Text>
                </Pressable>
                {!percentageCollapsed && <>
                <View style={{ flexDirection: 'row' }}>
                    <Text style={[s(70).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Phase</Text>
                    <Text style={[s(290).title, { fontSize: 11, borderRight: '1px solid #aeadaf' }]}>Description</Text>
                    {projectWeeks.map((week, i) => (
                        <Text key={i} style={[s(60).title, { fontSize: 10, borderRight: i === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                            {moment(week).format('M/D/YY')}
                        </Text>
                    ))}
                </View>
                
                {sections.map((sectionPhases, i) => (
                    <View key={i}>
                        {/* Discipline Header Row */}
                        <View style={{ flexDirection: 'row', backgroundColor: '#efefef' }}>
                            <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}></Text>
                            <Text style={[s(290).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf', textAlign: 'left' }]}>{sectionPhases[0]?.discipline || 'Other'}</Text>
                            {projectWeeks.map((week, j) => {
                                const weekTotal = calculateWeekTotal(week)
                                const disciplineWeekHours = sectionPhases.reduce((sum, phase) => {
                                    return sum + pNum(userData?.phaseHours?.[phase.code]?.weeks?.[week] || 0)
                                }, 0)
                                const percentage = weekTotal > 0 ? (disciplineWeekHours / weekTotal) * 100 : 0
                                return (
                                    <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                                        {percentage > 0 ? fNum(percentage, 1) + '%' : ''}
                                    </Text>
                                )
                            })}
                        </View>
                        {/* Individual Phase Rows */}
                        {expandedSections[i] !== false && sectionPhases.map((phase, j) => (
                            <View key={j} style={{ flexDirection: 'row' }}>
                                <Text style={[s(70).title, { borderTop: 0, fontSize: 11, borderRight: '1px solid #aeadaf' }]}>{phase.code}</Text>
                                <Text style={[s(290).title, { borderTop: 0, fontSize: 11, borderRight: '1px solid #aeadaf', textAlign: 'left' }]}>{phase.description}</Text>
                                {projectWeeks.map((week, k) => {
                                    const weekTotal = calculateWeekTotal(week)
                                    const phaseWeekHours = pNum(userData?.phaseHours?.[phase.code]?.weeks?.[week] || 0)
                                    const percentage = weekTotal > 0 ? (phaseWeekHours / weekTotal) * 100 : 0
                                    return (
                                        <Text key={k} style={[s(60).title, { borderTop: 0, fontSize: 11, borderRight: k === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                                            {percentage > 0 ? fNum(percentage, 1) + '%' : ''}
                                        </Text>
                                    )
                                })}
                            </View>
                        ))}
                    </View>
                ))}
                
                {/* Cumulative Percentage Row */}
                <View style={{ flexDirection: 'row' }}>
                    <Text style={[s(70).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: '1px solid #aeadaf' }]}>CUM Total</Text>
                    <Text style={[s(1290).title, { borderTop: 0, borderRight: '1px solid #aeadaf' }]}></Text>
                    {projectWeeks.map((week, j) => {
                        let cumHours = 0
                        for (let k = 0; k <= j; k++) {
                            cumHours += calculateWeekTotal(projectWeeks[k])
                        }
                        const grandTotal = calculateGrandTotal()
                        const cumPercent = grandTotal > 0 ? (cumHours / grandTotal) * 100 : 0
                        return (
                            <Text key={j} style={[s(60).title, { borderTop: 0, fontSize: 11, fontWeight: 'bold', borderRight: j === projectWeeks.length - 1 ? '1px solid #aeadaf' : 0 }]}>
                                {cumPercent > 0 ? fNum(cumPercent, 1) + '%' : ''}
                            </Text>
                        )
                    })}
                </View>
                </>}
            </View>
        </View>
    )
}


//to uninstall graphs
//npm uninstall react-native-gifted-charts
//npm uninstall react-native-svg
//npm uninstall react-native-linear-gradient
export default LaborGraphs;

